import { useState, useCallback, useEffect, CSSProperties } from 'react'
import useElement from './use-element'
const useKeyboardNavigation = <ListContainerElement extends HTMLElement, Item>({
  listItems,
  handleSelection,
  disabledList,
}: {
  listItems: Item[]
  handleSelection: (item: Item) => void
  disabledList?: Item[]
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isListContainerFocused, setIsListContainerFocused] = useState(false)
  const [listContainerNode, listContainerRef] = useElement<ListContainerElement>()
  const handleListKeyNavigation = useCallback(
    e => {
      const optionsLength = listItems.length
      switch (e.key) {
        case 'Up':
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex(activeIndex <= 0 ? optionsLength - 1 : activeIndex - 1)
          return
        case 'Down':
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex(activeIndex + 1 === optionsLength ? 0 : activeIndex + 1)
          return
        case 'Enter':
        case ' ': // Space
          e.preventDefault()
          if (!disabledList?.includes(listItems[activeIndex]))
            handleSelection(listItems[activeIndex])
          return
        case 'PageUp':
        case 'Home':
          e.preventDefault()
          setActiveIndex(0)
          return
        case 'PageDown':
        case 'End':
          e.preventDefault()
          setActiveIndex(optionsLength - 1)
          return
      }
    },
    [activeIndex, listItems, handleSelection, disabledList]
  )

  useEffect(() => {}, [])

  useEffect(() => {
    if (isListContainerFocused) {
      document.addEventListener('keydown', handleListKeyNavigation)
    } else {
      document.removeEventListener('keydown', handleListKeyNavigation)
    }

    return () => {
      document.removeEventListener('keydown', handleListKeyNavigation)
    }
  }, [isListContainerFocused, handleListKeyNavigation])

  useEffect(() => {
    if (listContainerNode) {
      const activeElement = listContainerNode.childNodes[activeIndex] as HTMLDivElement | undefined
      if (activeElement) {
        const scrollBottom = listContainerNode.clientHeight + listContainerNode.scrollTop
        const elementBottom = activeElement.offsetTop + activeElement.offsetHeight

        if (elementBottom > scrollBottom) {
          // element is below container's bottom
          listContainerNode.scrollTop = elementBottom - listContainerNode.clientHeight
        } else if (activeElement.offsetTop < listContainerNode.scrollTop) {
          // element is above container's top
          listContainerNode.scrollTop = activeElement.offsetTop
        }
      }
    }
  }, [activeIndex, listContainerNode])

  return {
    activeIndex,
    setActiveIndex,
    listContainerNode,
    listContainerProps: {
      ref: listContainerRef,
      onFocus: () => setIsListContainerFocused(true),
      onBlur: () => setIsListContainerFocused(false),
      /**
       * it is necessary that the container is a positioned element for the "scroll-into-view" behaviour to work, as the values of the container and the active element considered in the calculations above are relative to the container
       */
      style: { position: 'relative' } as CSSProperties,
    },
    isListContainerFocused,
  }
}

export default useKeyboardNavigation
